import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {StyledBudgetsRequestPageContainer, StyledBudgetsRequestPageContent} from './StyledBudgetsRequestsPage';
import EmptyOrTable from '../../components/EmptyOrTable';
import PageContainer from '../../components/PageContainer';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import WebSocket from '../../components/WebSocket';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import TableFilters from '../../components/pages/BudgetRequestsPage/TableFilters';
import ExpenseAgentChat from '../../components/pages/OverviewPage/AgentChatPanel';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import LoadMoreButton from '../../components/pages/TransactionsPage/LoadMoreButton';
import {useTabState} from '../../hooks';
import {
  accountingHelpers,
  amountHelpers,
  budgetHelpers,
  dateHelpers,
  objectHelpers,
  tabHelpers,
  tableHelpers,
  textHelpers,
  transactionsHelpers
} from '../../utils/helpers';
import {alertActions} from '../../state/actions/alert.actions';
import {budgetActions} from '../../state/actions/budget.actions';
import {backendWebsocketActionsConstants, systemHashKeysConstants} from '../../constants';
import routes from '../../routes/routes.json';

const dateColumnProps = accountingHelpers.getDateColumnProps();
const summaraizeKeyName = 'isMonthGrouping';
const columnProps = { width: 'calc(100% / 7)' };

const view = 'BudgetRequests';

const {ENABLE_WS} = systemHashKeysConstants;

const requestedBudgetStatuses = budgetHelpers.getRequestedBudgetStatuses();

const BudgetsRequestsPage = ({
  ...props
}) => {
  const [t] = useTranslation('main');
  const location = useLocation();

  // define specific hash key that enables websocket listening when present in URL
  const isHashEnabled = location.hash === ENABLE_WS;
  const [enabledWebSocketConnection, setEnabledWebSocketConnection] = useState(isHashEnabled);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const isOpenAgentChat = useSelector(state => state?.agent?.agentChatProps?.open || false);

  const title = t('pageTitles.budgetsRequests');

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
  } = useTabState({defaultQuery: { order_by: '-created_at' }, tabKeys: [view]});

  useEffect(() => {
    const query = objectHelpers.removeKeysFromObject(
      objectHelpers.getSearchParams(searchParams)
    );
    loadData({ initial: true, query });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.hash === ENABLE_WS) {
      setEnabledWebSocketConnection(true);
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpenAgentChat && !enabledWebSocketConnection) {
      setEnabledWebSocketConnection(true);
    }
  }, [isOpenAgentChat]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishTableLoading = () => {
    setLoading(prevState => ({...prevState, [view]: false}));
  }

  const loadData = ({
    query = null,
    initial = false,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setLoading(prevState => ({...prevState, [view]: true}));

    setQueries({ ...queries, [view]: viewQuery});

    // add requested statuses if not present in the query
    let requestQuery = { ...viewQuery };
    if (!query?.status?.length) requestQuery = { ...requestQuery, status: requestedBudgetStatuses };

    dispatch(
      budgetActions.getBudgetRequests({
        query: requestQuery,
        successCallback: (response) => {
          tabHelpers.handleStoreTableDataWithPrev({
            tableData: initial ? tableData : { ...tableData, [view]: { ...tableData[view], rows: [] } },
            setTableData,
            view,
            response,
            initial,
            updateCurrentPage
          });

          onSuccess?.();
          finishTableLoading();
        },
        errorCallback: finishTableLoading
      })
    );
  }

  const handleOnFilter = (filterQuery) => {
    tabHelpers.handleOnFilter({view, filterQuery, loadDataFunc: loadData, queries, setQueries});
    setSearchParams(
      objectHelpers.getSearchQueryParams({searchParams, filterQuery})
    );
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    ({
      [backendWebsocketActionsConstants.BUDGET_REQUEST_SUBMITTED]: () => {
        loadData({initial: true});
      },
    })[action]?.();

  };

  const columns = [
    {
      ...columnProps,
      className: 'no-wrap',
      dataIndex: 'name',
      render: (_, budget) => budgetHelpers.getBudgetName(budget),
      title: t('name'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      width: '80px',
      render: budgetHelpers.getOwner,
      title: t('Owner'),
    },
    {
      ...columnProps,
      dataIndex: 'active_approver',
      title: t('approver'),
      width: '80px',
      render: (value) => budgetHelpers.getUsersList(value, {size: 24})
    },
    {
      ...dateColumnProps,
      ...columnProps,
      dataIndex: 'date',
      width: '150px',
      render: (_, record) => dateHelpers.getDateFromISO(record?.created_at, record?.[summaraizeKeyName] ? 'MMM YYYY' : 'DD MMM YYYY'),
      title: t('date'),
    },
    {
      ...columnProps,
      dataIndex: 'expense_category',
      title: t('category')
    },
    {
      ...columnProps,
      dataIndex: 'reason',
      render: (value) => textHelpers.getTextWithTooltip(value, 80),
      title: t('reason')
    },
    {
      ...columnProps,
      align: 'right',
      dataIndex: 'limit_amount',
      render: (value) => <div className='amount'>{amountHelpers.getAmountWithCurrencyCode(value)}</div>,
      title: t('amount'),
    },
    {
      dataIndex: 'status',
      title: t('status'),
      render: (_, record) => budgetHelpers.getBudgetStatusMark(record)
    },
  ];

  const handleOnRowClick = (e, record) => {
    if (record?.[summaraizeKeyName]) return;
    navigate(`${routes.budgetsRequests}/${record.id}`);
  }

  const handleCloseAgent = (props) => {
    if (props?.confirmed) alertActions.success(t('budgets:alerts.budgetRequestSubmittedSuccess'), 6000, 450);
  }

  const tableProps = {
    rowClassName: (record) => transactionsHelpers.getRowClassName({
      groupedPropName: summaraizeKeyName,
      record
    }),
    onRow: (record, index) => ({
      onClick: (e) => handleOnRowClick(e, record, index),
    }),
    pagination: false,
  }

  const loadMoreProps = tableHelpers.getLoadMoreProps({tableData, view, loadData});

  return (
    <PageContainer {...props}>
      <PageDocumentDetails title={title} />
      <BalancePageHeader breadcrumbs={[{ title: t('budgets') }, { title: t('requests') }]} />
      <DashboardPageTransition>
        <StyledBudgetsRequestPageContainer>
          <StyledBudgetsRequestPageContent size='large' direction='vertical'>
            <TableFilters
              defaultQuery={queries[view]}
              exportAdditionalFields={{status: requestedBudgetStatuses}}
              onFilter={handleOnFilter}
              filterFields={['search', 'status', 'date_range', 'owner', 'approver']}
              tableType='requests'
            />
            <EmptyOrTable
              data={tableData?.[view]?.rows}
              loading={loading?.[view]}
              query={queries?.[view]}
              columns={columns}
              tableProps={tableProps}
            />
            <LoadMoreButton
              {...loadMoreProps}
              loading={loading?.[view] || loadMoreProps.loading}
              size='large'
            >
              {t('showMore')}
            </LoadMoreButton>
          </StyledBudgetsRequestPageContent>
        </StyledBudgetsRequestPageContainer>
      </DashboardPageTransition>

      <ExpenseAgentChat onClose={handleCloseAgent} />

      {enabledWebSocketConnection && <WebSocket onMessage={handleWebsocketMessage} />}

    </PageContainer>
  );
}

export default BudgetsRequestsPage;
